import dayjs from 'dayjs';
import Collapse from '@material-ui/core/Collapse';
import SettingsIcon from '@material-ui/icons/Settings';
import MediaSettingsFields from './MediaSettingsFields';
import {
  optionValues,
  getOptionsForModel,
  DEFAULT_IMAGE_MODELS,
  DEFAULT_VIDEO_MODELS,
  clampVideoDuration,
  pickListedModel,
  mentionLabelsForAsset,
  collectMentionLabels,
  mentionRegexFromLabels,
  assetByMention,
  matchedMentionLabels,
} from './helpers';

import { useEffect, useRef, useState, useMemo, useCallback, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import Avatar from '@material-ui/core/Avatar';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import LinearProgress from '@material-ui/core/LinearProgress';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import ImageIcon from '@material-ui/icons/Image';
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibrary';
import SaveIcon from '@material-ui/icons/Save';
import VideocamIcon from '@material-ui/icons/Videocam';
import HighQualityIcon from '@material-ui/icons/HighQuality';
import VisibilityIcon from '@material-ui/icons/Visibility';

import Loader from '../../../../components/Loader';
import AiImageDownloadDialog from '../../components/AiImageDownloadDialog';
import PageHeader from '../../components/PageHeader';
import {
  addToGifQueue,
  fetchAiAssets,
  fetchAiLibraryAssets,
  fetchAiRun,
  generateVideo,
  getAiImageUrl,
  refreshAiRun,
  resolveAssetTitles,
  saveAiLibraryAsset,
  sendAiMessage,
  upscaleAiImage,
  checkUpscaleStatus,
  updateRunDraft,
  updateAiRunSettings,
  fetchAiSettings,
} from '../../redux/actions/ai';
import { AIACTION_ATTEMPT } from '../../redux/constants';
import { fetchAllAprenders } from '../../redux/actions/aprender';
import { mapAprenderReference } from './aprenderAssets';

const useStyles = makeStyles((theme) => ({
  whiteTooltip: {
    backgroundColor: '#ffffff',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    border: '1px solid #e0e0e0',
    padding: 4,
  },
  whiteArrow: {
    color: '#ffffff',
  },
  root: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '70vh',
  },
  settingsPanel: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    background: '#fafafa',
    border: '1px solid #e0e0e0',
    width: '100%',
    maxWidth: 980,
    alignSelf: 'center',
    borderRadius: 8,
  },
  settingsActions: {
    marginTop: theme.spacing(1),
    display: 'flex',
    '& > *:not(:first-child)': {
      marginLeft: theme.spacing(1),
    },
  },
  runSummary: {
    width: '100%',
    maxWidth: 980,
    alignSelf: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 2),
    marginBottom: theme.spacing(2),
    border: '1px solid #e6e8eb',
    background: '#fff',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
  },
  chat: {
    width: '100%',
    maxWidth: 980,
    alignSelf: 'center',
    boxSizing: 'border-box',
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    background: '#f7f9fc',
    minHeight: 430,
    border: '1px solid #e6e8eb',
    borderRadius: 10,
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
  },
  row: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
  },
  userBubble: {
    alignSelf: 'flex-end',
    background: '#e8f2ff',
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    maxWidth: '72%',
    border: '1px solid #cfe3fb',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    [theme.breakpoints.down('xs')]: {
      maxWidth: '92%',
    },
  },
  assistantBubble: {
    alignSelf: 'flex-start',
    background: '#fff',
    padding: theme.spacing(1.5),
    borderRadius: 10,
    maxWidth: '76%',
    border: '1px solid #e2e5e9',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    [theme.breakpoints.down('xs')]: {
      maxWidth: '92%',
    },
  },
  messageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(0.75),
  },
  messageAuthor: {
    fontWeight: 600,
    color: '#3a3f45',
  },
  promptText: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    lineHeight: '1.6',
  },
  image: {
    maxWidth: '100%',
    maxHeight: 360,
    height: 'auto',
    display: 'block',
    borderRadius: 8,
    objectFit: 'contain',
  },
  meta: {
    color: '#69717a',
    whiteSpace: 'nowrap',
  },
  assetMeta: {
    opacity: 0.7,
    display: 'block',
    marginTop: theme.spacing(0.75),
  },
  composer: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
    '& > *:not(:first-child)': {
      marginLeft: 0,
    },
    [theme.breakpoints.down('xs')]: {
      alignItems: 'stretch',
      flexDirection: 'column',
    },
  },
  composerPanel: {
    width: '100%',
    maxWidth: 980,
    alignSelf: 'center',
    boxSizing: 'border-box',
    padding: theme.spacing(1.5),
    border: '1px solid #e0e3e7',
    borderRadius: 10,
    background: '#fff',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
  },
  imageActions: {
    marginTop: theme.spacing(1),
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  attachBtn: {
    alignSelf: 'flex-end',
    marginBottom: theme.spacing(0.25),
    border: '1px solid #d8dce1',
    background: '#fafafa',
  },
  previewStrip: {
    display: 'flex',
    gap: 8,
    padding: theme.spacing(1, 0),
    flexWrap: 'wrap',
  },
  previewThumb: {
    position: 'relative',
    width: 64,
    height: 64,
    borderRadius: 8,
    border: '1px solid #e0e0e0',
  },
  previewImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    borderRadius: 8,
  },
  removeBtn: {
    position: 'absolute',
    top: -2,
    right: -2,
    background: 'rgba(0,0,0,0.6)',
    color: '#fff',
    padding: 2,
    '&:hover': {
      background: 'rgba(0,0,0,0.8)',
    },
    '& svg': { fontSize: 14 },
  },
  richInputWrapper: {
    position: 'relative',
    border: '1px solid rgba(0,0,0,0.23)',
    borderRadius: 4,
    padding: '10px 14px',
    minHeight: 72,
    maxHeight: 200,
    overflowY: 'auto',
    cursor: 'text',
    background: '#fff',
    '&:hover': {
      borderColor: 'rgba(0,0,0,0.87)',
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  },
  richInputDisplay: {
    position: 'relative',
    zIndex: 2,
    minHeight: 48,
    lineHeight: '1.6',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    pointerEvents: 'none',
    userSelect: 'none',
    fontSize: 16,
    color: '#333',
  },
  richEditor: {
    position: 'relative',
    zIndex: 2,
    minHeight: 48,
    lineHeight: '1.6',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    outline: 'none',
    cursor: 'text',
    fontSize: 16,
    color: '#333',
  },
  editorMentionChip: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    maxWidth: '100%',
    height: 24,
    margin: '0 2px',
    padding: '0 8px',
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 16,
    color: theme.palette.primary.main,
    background: '#fff',
    fontSize: 13,
    lineHeight: '22px',
    verticalAlign: '-0.18em',
    whiteSpace: 'nowrap',
    cursor: 'default',
    '&:hover $editorMentionPreview': {
      display: 'block',
    },
  },
  editorMentionPreview: {
    display: 'none',
    position: 'absolute',
    left: '50%',
    bottom: 'calc(100% + 10px)',
    transform: 'translateX(-50%)',
    width: 220,
    maxHeight: 220,
    padding: 6,
    background: '#fff',
    borderRadius: 6,
    boxShadow: theme.shadows[4],
    zIndex: 1400,
    pointerEvents: 'none',
    '& img': {
      display: 'block',
      width: '100%',
      maxHeight: 208,
      objectFit: 'contain',
      borderRadius: 4,
    },
  },
  promptPlaceholder: {
    color: '#aaa',
    pointerEvents: 'none',
    position: 'absolute',
    top: 10,
    left: 14,
    fontSize: 16,
  },
  actionStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 180,
    '& .MuiButton-root': {
      whiteSpace: 'nowrap',
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  emptyState: {
    minHeight: 260,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
}));

const AssetItem = memo(({ asset, initialIsSelected, onToggle }) => {
  const [checked, setChecked] = useState(initialIsSelected);

  useEffect(() => {
    setChecked(initialIsSelected);
  }, [initialIsSelected]);

  const handleClick = useCallback(() => {
    setChecked((prev) => !prev);
    onToggle(asset);
  }, [asset, onToggle]);

  return (
    <Box onClick={handleClick} style={{ width: 120, cursor: 'pointer', textAlign: 'center', margin: 8 }}>
      {(asset.thumbnailUrl || asset.imageUrl) && (
        <img
          src={asset.thumbnailUrl || asset.imageUrl}
          alt={asset.title}
          style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 8 }}
          onError={(e) => {
            if (asset.imageUrl && e.target.src !== asset.imageUrl) {
              e.target.src = asset.imageUrl;
            } else {
              e.target.style.display = 'none';
            }
          }}
        />
      )}
      <Box display="flex" alignItems="center" justifyContent="center">
        <Checkbox checked={checked} color="primary" size="small" />
        <Typography variant="caption" noWrap style={{ flex: 1 }}>
          {asset.title}
        </Typography>
      </Box>
    </Box>
  );
});

export default function AiRunPage() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const bottomRef = useRef(null);
  const promptWrapperRef = useRef(null);
  const promptEditorRef = useRef(null);
  const { run, fetching, actionLoading, assets, libraryAssets, requestedRunId, drafts, settings } = useSelector(
    ({ admin }) => admin.ai,
  );
  const aprenders = useSelector(({ admin }) => admin.aprender?.aprenders);
  const activeRun = requestedRunId ? null : run;

  const [settingsOpen, setSettingsOpen] = useState(false);

  const [modelName, setModelName] = useState('gpt-image-2');
  const [resolution, setResolution] = useState('1k');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [videoModelName, setVideoModelName] = useState('seedance-2.5');
  const [videoResolution, setVideoResolution] = useState('720p');
  const [videoAspectRatio, setVideoAspectRatio] = useState('16:9');
  const [videoDuration, setVideoDuration] = useState(5);

  const imageModels = settings?.availableImageModels?.length > 0 ? settings.availableImageModels : DEFAULT_IMAGE_MODELS;
  const videoModels = settings?.availableVideoModels?.length > 0 ? settings.availableVideoModels : DEFAULT_VIDEO_MODELS;

  useEffect(() => {
    if (!settings) dispatch(fetchAiSettings());
  }, [dispatch, settings]);

  useEffect(() => {
    if (activeRun) {
      const nextImageModels =
        settings?.availableImageModels?.length > 0 ? settings.availableImageModels : DEFAULT_IMAGE_MODELS;
      const nextModelName = pickListedModel(nextImageModels, activeRun.modelName, 'gpt-image-2');
      const nextOptions = getOptionsForModel(nextModelName);
      setModelName(nextModelName);
      setResolution(
        optionValues(nextOptions.resolutions).includes(activeRun.aiResolution)
          ? activeRun.aiResolution
          : nextOptions.defaultResolution,
      );
      setAspectRatio(
        optionValues(nextOptions.aspectRatios).includes(activeRun.aiAspectRatio)
          ? activeRun.aiAspectRatio
          : nextOptions.defaultAspectRatio,
      );
      const nextVideoModels =
        settings?.availableVideoModels?.length > 0 ? settings.availableVideoModels : DEFAULT_VIDEO_MODELS;
      const nextVideoModel = pickListedModel(nextVideoModels, activeRun.aiVideoModelName, 'seedance-2.5');
      const nextVideoOptions = getOptionsForModel(nextVideoModel);
      setVideoModelName(nextVideoModel);
      setVideoResolution(
        optionValues(nextVideoOptions.resolutions).includes(activeRun.aiVideoResolution)
          ? activeRun.aiVideoResolution
          : nextVideoOptions.defaultResolution,
      );
      setVideoAspectRatio(
        optionValues(nextVideoOptions.aspectRatios).includes(activeRun.aiVideoAspectRatio)
          ? activeRun.aiVideoAspectRatio
          : nextVideoOptions.defaultAspectRatio,
      );
      setVideoDuration(clampVideoDuration(activeRun.aiVideoDuration));
    }
  }, [activeRun, settings]);

  const handleModelChange = (nextModelName) => {
    const nextOptions = getOptionsForModel(nextModelName);
    setModelName(nextModelName);
    setResolution((current) =>
      optionValues(nextOptions.resolutions).includes(current) ? current : nextOptions.defaultResolution,
    );
    setAspectRatio((current) =>
      optionValues(nextOptions.aspectRatios).includes(current) ? current : nextOptions.defaultAspectRatio,
    );
  };

  const handleVideoModelChange = (nextModelName) => {
    const nextOptions = getOptionsForModel(nextModelName);
    setVideoModelName(nextModelName);
    setVideoResolution((current) =>
      optionValues(nextOptions.resolutions).includes(current) ? current : nextOptions.defaultResolution,
    );
    setVideoAspectRatio((current) =>
      optionValues(nextOptions.aspectRatios).includes(current) ? current : nextOptions.defaultAspectRatio,
    );
  };

  const handleSaveSettings = async () => {
    if (!activeRun) return;
    await dispatch(
      updateAiRunSettings(activeRun._id, {
        modelName,
        aiResolution: resolution,
        aiAspectRatio: aspectRatio,
        aiVideoModelName: videoModelName,
        aiVideoResolution: videoResolution,
        aiVideoAspectRatio: videoAspectRatio,
        aiVideoDuration: videoDuration,
      }),
    );
    setSettingsOpen(false);
  };

  const [prompt, setPrompt] = useState('');
  const [submittedPrompt, setSubmittedPrompt] = useState('');
  const [submittedAssets, setSubmittedAssets] = useState([]);
  const [progress, setProgress] = useState(0);
  const [saveDialog, setSaveDialog] = useState({ open: false, index: null, title: '' });
  const [downloadDialog, setDownloadDialog] = useState({ open: false, index: null });
  const [localSubmitting, setLocalSubmitting] = useState(false);
  const [completionVisible, setCompletionVisible] = useState(false);
  const [visibleMessageCount, setVisibleMessageCount] = useState(null);
  const completionTimeoutRef = useRef(null);
  const wasGeneratingRef = useRef(false);

  const messages = useMemo(() => activeRun?.messages || [], [activeRun?.messages]);
  const isGenerating = messages.length > 0 && messages[messages.length - 1].status === 'generating';

  useEffect(() => {
    let timer;
    if (isGenerating || localSubmitting) {
      setCompletionVisible(false);
      timer = setInterval(() => {
        setProgress((oldProgress) => {
          if (oldProgress >= 95) return 95;
          const diff = Math.random() * 1.5 + 0.5; // increments by 0.5 to 2% every 500ms (takes ~30-40s to reach 95%)
          return Math.min(oldProgress + diff, 95);
        });
      }, 500);
    } else if (!completionVisible && !localSubmitting) {
      setProgress(0);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isGenerating, completionVisible, localSubmitting]);

  useEffect(() => {
    let interval;
    if (isGenerating && id) {
      interval = setInterval(() => {
        dispatch(refreshAiRun(id));
      }, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isGenerating, id, dispatch]);

  useEffect(
    () => () => {
      if (completionTimeoutRef.current) clearTimeout(completionTimeoutRef.current);
    },
    [],
  );

  const completeGenerationProgress = useCallback(
    () =>
      new Promise((resolve) => {
        setCompletionVisible(true);
        setProgress(100);
        if (completionTimeoutRef.current) clearTimeout(completionTimeoutRef.current);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            completionTimeoutRef.current = setTimeout(() => {
              setCompletionVisible(false);
              setVisibleMessageCount(null);
              setProgress(0);
              completionTimeoutRef.current = null;
              resolve();
            }, 700);
          });
        });
      }),
    [],
  );

  useEffect(() => {
    const finishedGenerating = wasGeneratingRef.current && !isGenerating;
    if (finishedGenerating && visibleMessageCount !== null && messages.length > visibleMessageCount) {
      completeGenerationProgress();
    }
    wasGeneratingRef.current = isGenerating;
  }, [completeGenerationProgress, isGenerating, messages.length, visibleMessageCount]);

  const [selectedAssets, setSelectedAssets] = useState([]);
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);

  const availableAssets = useMemo(() => {
    const arr = [];
    const assetDocs = assets?.docs || [];
    const libraryDocs = libraryAssets?.docs || [];
    const aprenderDocs = aprenders?.docs || [];
    const takenTitles = new Set();

    assetDocs.forEach((a) => {
      arr.push({ ...a, isLibraryAsset: false });
      if (a.title) takenTitles.add(a.title.toLowerCase());
    });
    libraryDocs.forEach((a) => {
      arr.push({ ...a, isLibraryAsset: true });
      if (a.title) takenTitles.add(a.title.toLowerCase());
    });
    aprenderDocs.forEach((row) => {
      const mapped = mapAprenderReference(row);
      if (!mapped) return;
      const aliases = mentionLabelsForAsset(mapped).filter((label) => !takenTitles.has(label.toLowerCase()));
      if (aliases.length === 0) return;
      aliases.forEach((label) => takenTitles.add(label.toLowerCase()));
      arr.push({ ...mapped, aliases, isLibraryAsset: false, isAprender: true });
    });
    return arr;
  }, [assets?.docs, libraryAssets?.docs, aprenders?.docs]);

  const loadingRun = requestedRunId === id;
  const isBusy = fetching || loadingRun || actionLoading || localSubmitting || isGenerating;
  const visibleMessages = useMemo(
    () => (visibleMessageCount === null ? messages : messages.slice(0, visibleMessageCount)),
    [messages, visibleMessageCount],
  );
  const displayedProgress = completionVisible ? 100 : progress;

  useEffect(() => {
    if (id) dispatch(fetchAiRun(id));
    dispatch(fetchAiAssets(1, 1000));
    dispatch(fetchAiLibraryAssets(1, 1000));
    dispatch(fetchAllAprenders({ page: 1, limit: 1000, orderBy: 'spanishName', orderDir: 'asc' }));
  }, [dispatch, id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [visibleMessages.length, actionLoading, isGenerating]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [mentionFilter, setMentionFilter] = useState('');
  // const [mentionCursorPos, setMentionCursorPos] = useState(0);
  const [mentionPreview, setMentionPreview] = useState(null);

  const draftInitialized = useRef(false);

  useEffect(() => {
    if (activeRun && !draftInitialized.current) {
      if (drafts?.[activeRun._id]) {
        const draftText = drafts[activeRun._id];
        setPrompt(draftText);
        // We'll render it as text, which might miss @mentions initially
        // but since they just typed it as text, that's fine for a draft.
        if (promptEditorRef.current) {
          promptEditorRef.current.innerText = draftText;
        }
      }
      draftInitialized.current = true;
    }
  }, [activeRun, drafts]);

  const getEditorText = () => (promptEditorRef.current?.innerText || '').replace(/\u00a0/g, ' ');

  const renderEditorContent = (promptText) => {
    const editor = promptEditorRef.current;
    if (!editor) return;

    editor.textContent = '';

    if (!promptText) return;

    const regex = mentionRegexFromLabels(collectMentionLabels(availableAssets));
    if (!regex) {
      editor.appendChild(document.createTextNode(promptText));
      return;
    }

    const parts = promptText.split(regex);

    parts.forEach((part) => {
      if (!part) return;

      if (part.startsWith('@')) {
        const asset = assetByMention(availableAssets, part);
        if (asset) {
          const chip = document.createElement('span');
          chip.className = classes.editorMentionChip;
          chip.contentEditable = 'false';
          chip.dataset.mentionChip = 'true';
          chip.appendChild(document.createTextNode(part));
          if (asset.thumbnailUrl) {
            chip.addEventListener('mouseenter', () => {
              const rect = chip.getBoundingClientRect();
              const showAbove = rect.top > 240;
              setMentionPreview({
                title: asset.title,
                url: asset.thumbnailUrl,
                left: rect.left + rect.width / 2,
                top: showAbove ? rect.top - 10 : rect.bottom + 10,
                transform: showAbove ? 'translate(-50%, -100%)' : 'translateX(-50%)',
              });
            });
            chip.addEventListener('mouseleave', () => {
              setMentionPreview(null);
            });
          }

          editor.appendChild(chip);
          return;
        }
      }

      editor.appendChild(document.createTextNode(part));
    });
  };

  const getCaretTextOffset = () => {
    const editor = promptEditorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) return 0;

    const range = selection.getRangeAt(0);
    const val = getEditorText();
    if (!editor.contains(range.endContainer)) return val.length;

    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(editor);
    preCaretRange.setEnd(range.endContainer, range.endOffset);

    const textNodeCursor = preCaretRange.toString().length;

    // Map textNodeCursor (which ignores newlines) to the real innerText offset
    let realCursor = 0;
    let currentTextNodeCount = 0;
    let found = false;
    for (let i = 0; i < val.length; i++) {
      if (currentTextNodeCount === textNodeCursor) {
        realCursor = i;
        found = true;
        break;
      }
      if (val[i] !== '\n') {
        currentTextNodeCount++;
      }
    }
    if (!found) {
      realCursor = val.length;
    }

    return realCursor;
  };

  const setEditorCaret = (offset) => {
    const editor = promptEditorRef.current;
    if (!editor) return;

    const val = prompt; // prompt string has the newlines
    const newlinesBefore = (val.slice(0, offset).match(/\n/g) || []).length;
    const textNodeOffset = Math.max(0, offset - newlinesBefore);

    const selection = window.getSelection();
    const range = document.createRange();
    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
    let current = 0;
    let node = walker.nextNode();

    while (node) {
      const length = node.textContent.length;
      if (current + length >= textNodeOffset) {
        const chip = node.parentElement?.closest('[data-mention-chip]');
        if (chip) {
          if (textNodeOffset - current < length / 2) {
            range.setStartBefore(chip);
          } else {
            range.setStartAfter(chip);
          }
        } else {
          range.setStart(node, Math.max(0, textNodeOffset - current));
        }
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        return;
      }
      current += length;
      node = walker.nextNode();
    }

    range.selectNodeContents(editor);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const updateMentionState = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      setAnchorEl(null);
      return;
    }
    const range = selection.getRangeAt(0);
    const node = range.startContainer;
    if (!node || node.nodeType !== Node.TEXT_NODE) {
      setAnchorEl(null);
      return;
    }
    // Walk backwards in the current text node from cursor position
    const textBeforeCursor = node.textContent.slice(0, range.startOffset);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    if (atIndex === -1) {
      setAnchorEl(null);
      return;
    }
    // Make sure there's no space between @ and the cursor (so @Word not @ Word)
    const filterText = textBeforeCursor.slice(atIndex + 1);
    if (filterText.includes(' ') && filterText.trim() !== filterText) {
      setAnchorEl(null);
      return;
    }
    const lowerFilter = filterText.toLowerCase();
    const matches = availableAssets.filter((a) => a.title.toLowerCase().includes(lowerFilter));
    if (matches.length > 0) {
      setMentionFilter(lowerFilter);
      // Compute global cursor position for mention replacement
      const editor = promptEditorRef.current;
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(editor);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      // const globalCursor = preCaretRange.toString().length;
      // setMentionCursorPos(globalCursor - filterText.length);
      setAnchorEl(promptWrapperRef.current);
    } else {
      setAnchorEl(null);
    }
  };

  const handlePromptInput = () => {
    const val = getEditorText();
    setPrompt(val);
    updateMentionState();
    if (activeRun) {
      dispatch(updateRunDraft(activeRun._id, val));
    }
  };

  const handlePromptClick = () => {
    setPrompt(getEditorText());
    updateMentionState();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const handleMentionSelect = (asset) => {
    setAnchorEl(null);
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const node = range.startContainer;
    if (!node || node.nodeType !== Node.TEXT_NODE) return;

    // Find where @ is in this text node
    const textBeforeCursor = node.textContent.slice(0, range.startOffset);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    if (atIndex === -1) return;
    const query = textBeforeCursor.slice(atIndex + 1).toLowerCase();
    const labels = mentionLabelsForAsset(asset);
    const mention = labels.find((label) => label.toLowerCase().startsWith(query)) || labels[0] || asset.title;

    const scrollPos = promptWrapperRef.current ? promptWrapperRef.current.scrollTop : 0;

    // Select from the @ to the cursor and replace with the chosen title + space
    const replaceRange = document.createRange();
    replaceRange.setStart(node, atIndex);
    replaceRange.setEnd(range.endContainer, range.endOffset);
    selection.removeAllRanges();
    selection.addRange(replaceRange);
    document.execCommand('insertText', false, `@${mention} `);

    // Rebuild chips from current text
    const newVal = getEditorText();
    setPrompt(newVal);
    renderEditorContent(newVal);

    // Restore scroll
    if (promptWrapperRef.current) {
      promptWrapperRef.current.scrollTop = scrollPos;
    }
  };

  const handleSend = async () => {
    const text = prompt.trim();
    if (!text || !id || isBusy) return;
    setLocalSubmitting(true);
    setSubmittedPrompt(text);
    setSubmittedAssets(selectedAssets);
    setPrompt('');
    if (promptEditorRef.current) promptEditorRef.current.textContent = '';
    setSelectedAssets([]);
    setVisibleMessageCount(messages.length + 1);
    dispatch({ type: AIACTION_ATTEMPT });

    try {
      let finalAssetIds = [...selectedAssets.map((a) => a._id)];

      const matches = matchedMentionLabels(text, availableAssets);

      if (matches.length > 0) {
        const resolved = await resolveAssetTitles(matches);
        resolved.forEach((a) => {
          if (!finalAssetIds.includes(a._id)) finalAssetIds.push(a._id);
        });
      }

      const generatedRun = await dispatch(sendAiMessage(id, text, [], finalAssetIds));
      if (generatedRun) {
        const generatedMessages = generatedRun.messages || [];
        const lastMessage = generatedMessages[generatedMessages.length - 1];
        if (lastMessage?.status === 'generating') return;
        await completeGenerationProgress();
        // Re-fetch the run to guarantee fresh signed URLs are loaded.
        // The sendAiMessage response should already contain them, but
        // re-fetching mirrors the reliable page-refresh path and ensures
        // images appear immediately (especially for wavespeed-generated images).
        dispatch(refreshAiRun(id));
      } else {
        setVisibleMessageCount(null);
      }
    } finally {
      setLocalSubmitting(false);
    }
  };

  const toggleAssetSelection = useCallback((asset) => {
    setSelectedAssets((prev) => {
      const isSelected = prev.some((a) => a._id === asset._id);
      if (isSelected) {
        return prev.filter((a) => a._id !== asset._id);
      }
      return [...prev, asset];
    });
  }, []);

  const handleDownload = useCallback((messageIndex) => {
    setDownloadDialog({ open: true, index: messageIndex });
  }, []);

  const handleSave = useCallback(
    (messageIndex) => {
      if (!activeRun || fetching) return;
      setSaveDialog({ open: true, index: messageIndex, title: '' });
    },
    [activeRun, fetching],
  );

  const confirmSave = async () => {
    if (saveDialog.index !== null && activeRun && !fetching) {
      const saved = await dispatch(saveAiLibraryAsset(activeRun._id, saveDialog.index, saveDialog.title));
      if (saved) {
        setSaveDialog({ open: false, index: null, title: '' });
      }
    }
  };

  const handleGenerateVideo = async () => {
    const text = prompt.trim();
    if (!text || !id || isBusy) return;
    setLocalSubmitting(true);
    setSubmittedPrompt(text);
    setSubmittedAssets(selectedAssets);
    setPrompt('');
    if (promptEditorRef.current) promptEditorRef.current.textContent = '';
    setSelectedAssets([]);
    setVisibleMessageCount(messages.length + 1);
    dispatch({ type: AIACTION_ATTEMPT });

    try {
      let finalAssetIds = [...selectedAssets.map((a) => a._id)];

      const matches = matchedMentionLabels(text, availableAssets);

      if (matches.length > 0) {
        const resolved = await resolveAssetTitles(matches);
        resolved.forEach((a) => {
          if (!finalAssetIds.includes(a._id)) finalAssetIds.push(a._id);
        });
      }

      const generatedRun = await dispatch(generateVideo(id, text, finalAssetIds));
      if (generatedRun) {
        const generatedMessages = generatedRun.messages || [];
        const lastMessage = generatedMessages[generatedMessages.length - 1];
        if (lastMessage?.status === 'generating') return;
        await completeGenerationProgress();
        // Re-fetch to ensure fresh signed URLs (same as image generation)
        dispatch(refreshAiRun(id));
      } else {
        setVisibleMessageCount(null);
      }
    } finally {
      setLocalSubmitting(false);
    }
  };

  const handleAddToGifQueue = useCallback(
    async (messageIndex, videoUrl) => {
      if (!activeRun) return;
      const seconds = activeRun.messages?.[messageIndex]?.videoDuration;
      await dispatch(addToGifQueue([videoUrl], activeRun._id, messageIndex, seconds || undefined));
    },
    [activeRun, dispatch],
  );

  const { mentionRegex, availableAssetsDict } = useMemo(() => {
    if (!availableAssets || availableAssets.length === 0) {
      return { mentionRegex: null, availableAssetsDict: {} };
    }
    const titles = collectMentionLabels(availableAssets);
    const dict = {};
    availableAssets.forEach((asset) => {
      mentionLabelsForAsset(asset).forEach((label) => {
        dict[label.toLowerCase()] = asset;
      });
    });

    return {
      mentionRegex: mentionRegexFromLabels(titles),
      availableAssetsDict: dict,
    };
  }, [availableAssets]);

  const renderPromptWithMentions = useCallback(
    (promptText) => {
      if (!promptText) return null;
      if (!mentionRegex) return <span>{promptText}</span>;

      const parts = promptText.split(mentionRegex);

      return (
        <span style={{ display: 'inline' }}>
          {parts.map((part, index) => {
            if (part && part.startsWith('@')) {
              const titleKey = part.substring(1).toLowerCase();
              const asset = availableAssetsDict[titleKey];
              if (asset) {
                return (
                  // pointerEvents: auto on this span lets hover pierce the parent's pointerEvents: none
                  <span
                    key={index}
                    data-mention-chip
                    contentEditable={false}
                    style={{ pointerEvents: 'auto', display: 'inline-flex', verticalAlign: 'baseline' }}
                  >
                    <Tooltip
                      title={
                        asset.thumbnailUrl || asset.imageUrl ? (
                          <img
                            src={asset.thumbnailUrl || asset.imageUrl}
                            alt={asset.title}
                            className=""
                            style={{
                              width: 180,
                              maxHeight: 180,
                              objectFit: 'contain',
                              borderRadius: 4,
                              display: 'block',
                            }}
                            onError={(e) => {
                              if (asset.imageUrl && e.target.src !== asset.imageUrl) {
                                e.target.src = asset.imageUrl;
                              } else {
                                e.target.style.display = 'none';
                              }
                            }}
                          />
                        ) : (
                          ''
                        )
                      }
                      placement="top"
                      arrow
                      interactive
                      classes={{ tooltip: classes.whiteTooltip, arrow: classes.whiteArrow }}
                    >
                      <Chip
                        label={part}
                        size="small"
                        color="primary"
                        variant="outlined"
                        style={{ cursor: 'default', margin: '0 2px', verticalAlign: 'baseline' }}
                      />
                    </Tooltip>
                  </span>
                );
              }
            }
            return <span key={index}>{part}</span>;
          })}
        </span>
      );
    },
    [mentionRegex, availableAssetsDict, classes],
  );

  const assetDialogContent = useMemo(() => {
    if (!assetPickerOpen) return null;
    const selectedSet = new Set(selectedAssets.map((a) => a._id));
    return availableAssets.map((asset) => (
      <AssetItem
        key={asset._id}
        asset={asset}
        initialIsSelected={selectedSet.has(asset._id)}
        onToggle={toggleAssetSelection}
      />
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableAssets, assetPickerOpen, toggleAssetSelection]);

  return (
    <div className={classes.root}>
      {(fetching || loadingRun) && <Loader fetching={fetching || loadingRun} />}
      <PageHeader
        disableSearch
        title={activeRun ? `AI Run - ${activeRun.modelName || ''}` : 'AI Run'}
        back
        handleBack={() => history.push('/superadmin/ai')}
        options={
          <IconButton title="Run settings" onClick={() => setSettingsOpen((v) => !v)}>
            <SettingsIcon />
          </IconButton>
        }
      />

      <Collapse in={settingsOpen} style={{ width: '100%', maxWidth: 980, alignSelf: 'center' }}>
        <div className={classes.settingsPanel}>
          <Typography variant="subtitle1" gutterBottom>
            Run Settings
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            These settings apply only to this run. Image settings are used for Generate Image. Video settings are used
            for Generate Video.
          </Typography>
          <MediaSettingsFields
            imageModels={imageModels}
            imageModel={modelName}
            imageResolution={resolution}
            imageAspectRatio={aspectRatio}
            onImageModelChange={handleModelChange}
            onImageResolutionChange={setResolution}
            onImageAspectRatioChange={setAspectRatio}
            videoModels={videoModels}
            videoModel={videoModelName}
            videoResolution={videoResolution}
            videoAspectRatio={videoAspectRatio}
            videoDuration={videoDuration}
            onVideoModelChange={handleVideoModelChange}
            onVideoResolutionChange={setVideoResolution}
            onVideoAspectRatioChange={setVideoAspectRatio}
            onVideoDurationChange={setVideoDuration}
            disabled={actionLoading}
          />

          <div className={classes.settingsActions}>
            <Button color="primary" variant="contained" onClick={handleSaveSettings} disabled={actionLoading}>
              Save
            </Button>
            <Button onClick={() => setSettingsOpen(false)}>Close</Button>
          </div>
        </div>
      </Collapse>

      {activeRun && (
        <Paper className={classes.runSummary} elevation={0}>
          <Typography variant="body2" color="textSecondary">
            Status: <strong>{activeRun.status}</strong>
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Created {activeRun.createdAt ? dayjs(activeRun.createdAt).format('MMM D, YYYY h:mm A') : '-'}
          </Typography>
        </Paper>
      )}

      <Paper className={classes.chat} elevation={0}>
        {useMemo(() => {
          if (visibleMessages.length === 0 && !fetching && !actionLoading) {
            return (
              <div className={classes.emptyState}>
                <Typography color="textSecondary">No messages in this run yet.</Typography>
              </div>
            );
          }
          return visibleMessages.map((msg, index) => {
            if (msg.status === 'generating') return null;
            return (
              <div key={msg._id || index} className={classes.row}>
                {msg.role === 'user' ? (
                  <div className={classes.userBubble}>
                    <div className={classes.messageHeader}>
                      <Typography variant="caption" className={classes.messageAuthor}>
                        You
                      </Typography>
                      <Typography variant="caption" className={classes.meta}>
                        {msg.createdAt ? dayjs(msg.createdAt).format('h:mm A') : ''}
                      </Typography>
                    </div>
                    <div className={classes.promptText}>{renderPromptWithMentions(msg.prompt)}</div>
                    {(msg.assetIds?.length > 0 || msg.resolvedAssetUrls?.length > 0) && (
                      <Typography variant="caption" color="textSecondary" className={classes.assetMeta}>
                        Assets: {msg.resolvedAssetUrls?.length || msg.assetIds?.length} reference image(s)
                      </Typography>
                    )}
                  </div>
                ) : (
                  <div className={classes.assistantBubble}>
                    <div className={classes.messageHeader}>
                      <Typography variant="caption" className={classes.messageAuthor}>
                        AI
                      </Typography>
                      <Typography variant="caption" className={classes.meta}>
                        {msg.createdAt ? dayjs(msg.createdAt).format('h:mm A') : ''}
                      </Typography>
                    </div>
                    {msg.videoUrl ? (
                      <>
                        <video
                          className={classes.image}
                          src={msg.videoUrl}
                          controls
                          autoPlay
                          loop
                          muted
                          style={{ maxWidth: '100%', borderRadius: 8 }}
                        />
                        <div className={classes.imageActions}>
                          <Button
                            size="small"
                            color="secondary"
                            variant="contained"
                            onClick={() => handleAddToGifQueue(index, msg.videoUrl)}
                            disabled={isBusy}
                          >
                            Add to GIF Queue
                          </Button>
                        </div>
                      </>
                    ) : msg.imageUrl ? (
                      <>
                        <img className={classes.image} src={msg.imageUrl} alt="Generated illustration" />
                        <div className={classes.imageActions}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            onClick={() => window.open(msg.imageUrl, '_blank')}
                            disabled={isBusy}
                          >
                            View Media
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<HighQualityIcon />}
                            onClick={() => handleDownload(index)}
                            disabled={isBusy}
                          >
                            Upscale
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<GetAppIcon />}
                            onClick={() => handleDownload(index)}
                            disabled={isBusy}
                          >
                            Download
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<SaveIcon />}
                            onClick={() => handleSave(index)}
                            disabled={isBusy}
                          >
                            Save
                          </Button>
                        </div>
                      </>
                    ) : msg.status === 'failed' ? (
                      <Typography color="error">Generation failed. {msg.prompt || ''}</Typography>
                    ) : (
                      <Typography color="textSecondary">Processing...</Typography>
                    )}
                  </div>
                )}
              </div>
            );
          });
        }, [
          visibleMessages,
          fetching,
          actionLoading,
          classes,
          isBusy,
          renderPromptWithMentions,
          handleAddToGifQueue,
          handleDownload,
          handleSave,
        ])}
        {actionLoading && Boolean(submittedPrompt) && (
          <div className={classes.row}>
            <div className={classes.userBubble}>
              <div className={classes.messageHeader}>
                <Typography variant="caption" className={classes.messageAuthor}>
                  You
                </Typography>
                <Typography variant="caption" className={classes.meta}>
                  Sending...
                </Typography>
              </div>
              <div className={classes.promptText}>{renderPromptWithMentions(submittedPrompt)}</div>
              {submittedAssets?.length > 0 && (
                <Typography variant="caption" color="textSecondary" className={classes.assetMeta}>
                  Assets: {submittedAssets.length} reference image(s)
                </Typography>
              )}
            </div>
          </div>
        )}
        {(isGenerating || localSubmitting || completionVisible) && displayedProgress > 0 && (
          <Box display="flex" justifyContent="flex-start" width="100%" my={4}>
            <div className={classes.assistantBubble} style={{ minWidth: 300, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {displayedProgress < 30
                  ? 'Initializing...'
                  : displayedProgress < 70
                  ? 'Generating image...'
                  : 'Finalizing...'}
              </Typography>
              <Box display="flex" alignItems="center" mt={1}>
                <Box width="100%" mr={1}>
                  <LinearProgress variant="determinate" value={displayedProgress} />
                </Box>
                <Box minWidth={35}>
                  <Typography variant="body2" color="textSecondary">
                    {`${Math.round(displayedProgress)}%`}
                  </Typography>
                </Box>
              </Box>
            </div>
          </Box>
        )}
        <div ref={bottomRef} />
      </Paper>

      <Paper className={classes.composerPanel} elevation={1}>
        <div className={classes.composer}>
          <IconButton
            className={classes.attachBtn}
            title="Select from Native Assets"
            onClick={() => setAssetPickerOpen(true)}
            disabled={isBusy}
          >
            <PhotoLibraryIcon />
          </IconButton>

          <div style={{ flex: 1 }}>
            <div
              ref={promptWrapperRef}
              className={classes.richInputWrapper}
              onMouseLeave={() => setMentionPreview(null)}
              onClick={() => {
                if (promptEditorRef.current) promptEditorRef.current.focus();
              }}
            >
              {!prompt && <span className={classes.promptPlaceholder}>Describe the illustration…</span>}
              <div
                id="run-prompt-editor"
                ref={promptEditorRef}
                className={classes.richEditor}
                contentEditable={!isBusy}
                suppressContentEditableWarning
                onInput={handlePromptInput}
                onClick={handlePromptClick}
                onPaste={handlePaste}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (!isBusy) handleSend();
                  }
                }}
              />
            </div>
            <Popper
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              placement="top-start"
              style={{ zIndex: 1300, width: anchorEl ? anchorEl.clientWidth : undefined, marginBottom: 8 }}
            >
              {Boolean(anchorEl) && (
                <Paper elevation={3} style={{ maxHeight: 200, overflow: 'auto' }}>
                  <List dense>
                    {availableAssets
                      .filter((asset) => {
                        const query = mentionFilter.toLowerCase();
                        if (!query) return true;
                        return (
                          asset.title.toLowerCase().includes(query) ||
                          mentionLabelsForAsset(asset).some((label) => label.toLowerCase().includes(query))
                        );
                      })
                      .map((asset) => (
                        <ListItem button key={asset._id} onClick={() => handleMentionSelect(asset)}>
                          {(asset.thumbnailUrl || asset.imageUrl) && (
                            <ListItemAvatar>
                              <Avatar
                                variant="rounded"
                                src={asset.thumbnailUrl || asset.imageUrl}
                                imgProps={{
                                  onError: (e) => {
                                    if (asset.imageUrl && e.target.src !== asset.imageUrl) {
                                      e.target.src = asset.imageUrl;
                                    } else {
                                      e.target.style.display = 'none';
                                    }
                                  },
                                }}
                              />
                            </ListItemAvatar>
                          )}
                          <ListItemText primary={asset.title} />
                          {asset.isLibraryAsset && (
                            <Chip
                              label="AI"
                              size="small"
                              color="primary"
                              variant="outlined"
                              style={{
                                height: 20,
                                fontSize: 10,
                                marginLeft: 8,
                                fontWeight: 'bold',
                              }}
                            />
                          )}
                        </ListItem>
                      ))}
                  </List>
                </Paper>
              )}
            </Popper>
            {selectedAssets.length > 0 && (
              <div className={classes.previewStrip}>
                {selectedAssets.map((asset) => (
                  <div key={asset._id} className={classes.previewThumb} title={asset.title}>
                    {(asset.thumbnailUrl || asset.imageUrl) && (
                      <img
                        className={`${classes.previewImg} zoomable-thumbnail`}
                        src={asset.thumbnailUrl || asset.imageUrl}
                        alt={asset.title}
                        onError={(e) => {
                          if (asset.imageUrl && e.target.src !== asset.imageUrl) {
                            e.target.src = asset.imageUrl;
                          } else {
                            e.target.style.display = 'none';
                          }
                        }}
                      />
                    )}
                    <IconButton className={classes.removeBtn} size="small" onClick={() => toggleAssetSelection(asset)}>
                      <CloseIcon />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className={classes.actionStack}>
            <Button
              color="primary"
              variant="contained"
              startIcon={<ImageIcon />}
              onClick={handleSend}
              disabled={isBusy || !prompt.trim()}
            >
              Generate Image
            </Button>
            <Button
              color="secondary"
              variant="contained"
              startIcon={<VideocamIcon />}
              onClick={handleGenerateVideo}
              disabled={isBusy || !prompt.trim()}
            >
              Generate Video
            </Button>
          </div>
        </div>
      </Paper>

      {mentionPreview && (
        <div
          style={{
            position: 'fixed',
            left: mentionPreview.left,
            top: mentionPreview.top,
            transform: mentionPreview.transform,
            zIndex: 2000,
            width: 180,
            maxHeight: 180,
            padding: 6,
            background: '#fff',
            borderRadius: 6,
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            pointerEvents: 'none',
          }}
        >
          <img
            src={mentionPreview.url}
            alt={mentionPreview.title}
            style={{ width: '100%', maxHeight: 168, objectFit: 'contain', borderRadius: 4, display: 'block' }}
          />
        </div>
      )}

      <Dialog open={assetPickerOpen} onClose={() => setAssetPickerOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Select Assets</DialogTitle>
        <DialogContent>
          {availableAssets.length === 0 ? (
            <Typography color="textSecondary">No assets found in references.</Typography>
          ) : (
            <Box display="flex" flexWrap="wrap" gap={2}>
              {assetDialogContent}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssetPickerOpen(false)} color="primary">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <AiImageDownloadDialog
        open={downloadDialog.open}
        imageUrl={downloadDialog.index !== null ? activeRun?.messages?.[downloadDialog.index]?.imageUrl : ''}
        onClose={() => setDownloadDialog({ open: false, index: null })}
        onUpscale={() => ({
          initiate: () => upscaleAiImage(activeRun._id, downloadDialog.index),
          checkStatus: () => checkUpscaleStatus(activeRun._id, downloadDialog.index),
        })}
        onDownloadOriginal={() => getAiImageUrl(activeRun._id, downloadDialog.index, true)}
      />

      <Dialog
        open={saveDialog.open}
        onClose={() => setSaveDialog({ open: false, index: null, title: '' })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Save to AI Assets</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Image Title"
            fullWidth
            value={saveDialog.title}
            onChange={(e) => setSaveDialog({ ...saveDialog, title: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && saveDialog.title.trim()) confirmSave();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialog({ open: false, index: null, title: '' })}>Cancel</Button>
          <Button onClick={confirmSave} color="primary" disabled={!saveDialog.title.trim() || isBusy}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
